import React from "react";
import styled from "styled-components";
import { Select, MenuItem } from "@mui/material";
import { portalColors } from "src/components";
import UnreadCount from "src/scenes/Chat/components/children/UnreadCount";
import { makeStyles } from "@mui/styles";
import { UserKeys } from "src/constants/userDetails";
import { GROUP_CHAT, ONE_ON_ONE_CHAT, USER_TYPE_CLIENTS, USER_TYPE_EMPLOYEES } from "src/constants/chat";
import { 
    CREATE_GROUP_CHAT, 
    MESSAGE_CLIENTS,
    MESSAGE_EMPLOYEES,
    FULL_ACCESS_BUSINESS_CHAT,
    isChatActionAllowed 
} from "src/constants/permissions";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";
import UserSingleIcon from "src/img/new/new-1-on-1-chat.svg";
import UserGroupIcon from "src/img/new/new-group-chat.svg";
import IconButton from "src/scenes/Chat/components/IconButton";
import UserIcon from "src/img/new/user-2.svg";
import { LEADERSHIP_ROLES, hasPermissions } from "src/constants/permissions";

import { 
    StyledText, CreateChatButtons,
} from "src/scenes/Chat/components/styled/chatListStyled";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const useStyles = makeStyles(() => ({
    outlined: {
        "&:focus": {
            outline: "none",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
        },
    },
    listItemSecondary: {
        color: "var(--calendar-text, #171A22)"
    }
}));

const StyledMenuItem = styled(MenuItem)`
    .unread-count {
        position: absolute;
        right: 15px;
    }
`;


const ActiveChatListSelect = styled(Select)`
    .MuiSelect-selectMenu{
        display: flex;
    }

    .MuiSelect-select {
        position: relative;
        display: flex;
        align-items: center;
    }

    .MuiSelect-outlined {
        background: ${portalColors.surfaceSoft};
        color: ${portalColors.text};
        border-radius: 7px;
    }

    .MuiOutlinedInput-notchedOutline {
        border-color: ${portalColors.border} !important;
    }

    .MuiSvgIcon-root {
        color: ${portalColors.text};
    }

    .unread-count {
        position: absolute;
        right: 30px;
    }
`;

const LeadershipRolesButton = styled.div`
    width: 100%;
    height: 40px;
    min-width: 0;
    box-sizing: border-box;
    background: ${portalColors.surface};
    box-shadow: ${portalColors.shadow};
    border: 1px solid ${portalColors.border};
    border-radius: 7px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
`;

const LeadershipInfo = styled.div`
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    min-width: 0;
    overflow: hidden;
    font-weight: 500;
    font-size: 0.9rem;
    white-space: nowrap;
`;

const ManageButton = styled.button`
    width: 77px;
    height: 26px;
    flex: 0 0 77px;
    background: ${portalColors.actionPrimary};
    color: ${portalColors.actionPrimaryText};
    border: 1px solid ${portalColors.actionPrimary};
    border-radius: 7px;
    font-size: 0.8rem;
    cursor: pointer;
    box-shadow: ${portalColors.shadow};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 10px 0 0;
`;

const LeadershipIcon = styled.img`
    filter: none;
`;

const selectStyle = {
    width: 227,
    height: 40,
    fontSize: "0.9rem",
    fontFamily: "Manrope",
    fontWeight: "600",
    color: "var(--calendar-text, #171A22)",
    backgroundColor: "var(--calendar-surface-secondary, #F3F5FB)",
    border: "1px solid var(--calendar-border, #DBE5ED)",
    boxShadow: "var(--calendar-shadow, 0px 3px 6px #00000029)",
    borderRadius: 7,
    "&:focus": {
        backgroundColor: "transparent"
    }
};

const ActiveChats = (props) => {
    const classes = useStyles();
    const {
        currentUser,
        showCreateChatModalOrShowLeadershipEmployees,
        selectedChatListTypeOrLeadershipUuid,
        handleChatListSelectChange,
        activeChatsTotalUnread,
        leadershipRoles,
        activeChatListDetails,
        isFetchingActiveChats
    } = props;
    const userPermissions = currentUser[UserKeys.PERMISSIONS];

    const canSeeLeadershipRoles = hasPermissions([LEADERSHIP_ROLES], userPermissions);

    const handleSelectChange = (event) => {
        handleChatListSelectChange(event.target.value);
    };

    const showCreateOneToOneChatButton = () => {
        if (selectedChatListTypeOrLeadershipUuid === USER_TYPE_EMPLOYEES) {
            return isChatActionAllowed(MESSAGE_EMPLOYEES, userPermissions);
        } else if (selectedChatListTypeOrLeadershipUuid === USER_TYPE_CLIENTS) {
            return isChatActionAllowed(MESSAGE_CLIENTS, userPermissions);
        } else {
            return true; // Always allowed to reach out to leaders
        }
    };
    
    const showCreateGroupChatButton = () => {
        const userLeadershipRoles = currentUser[UserKeys.LEADERSHIP_ROLES];

        if (selectedChatListTypeOrLeadershipUuid === USER_TYPE_EMPLOYEES) {
            return isChatActionAllowed(MESSAGE_EMPLOYEES, userPermissions);
        } else if (selectedChatListTypeOrLeadershipUuid === USER_TYPE_CLIENTS) {
            return isChatActionAllowed(MESSAGE_CLIENTS, userPermissions);
        } else {
            //leadershipRole

            // Check if selectedChatListTypeOrLeadershipUuid exists in userLeadershipRoles
            return userLeadershipRoles.some(userLeadershipRole =>
                selectedChatListTypeOrLeadershipUuid === userLeadershipRole.uuid
            );
        }
    };

    const getUnreadCount = (selectedChatListTypeOrLeadershipUuid) => {

        const isNonLeadership = [USER_TYPE_CLIENTS, USER_TYPE_EMPLOYEES].includes(selectedChatListTypeOrLeadershipUuid);
        try 
        {
            if (isNonLeadership) {
                const nonLeadershipType = activeChatsTotalUnread.find(chatList => chatList.label === selectedChatListTypeOrLeadershipUuid);
                return nonLeadershipType ? nonLeadershipType.totalUnreadCount : 0;
            }

            //leadership
            const findLeadershipRole = leadershipRoles.find(role => role.uuid === selectedChatListTypeOrLeadershipUuid);
            if (!findLeadershipRole) {
                console.warn(`Leadership Role: ${selectedChatListTypeOrLeadershipUuid} not found when trying to get the total unread count.`, leadershipRoles, selectedChatListTypeOrLeadershipUuid);
                return 0;
            }
            
            const leadershipList = activeChatsTotalUnread.find(chatList => chatList.label === findLeadershipRole.uuid);
            return leadershipList ? leadershipList.totalUnreadCount : 0;
        } catch (e) {
            console.warn("Error in getting total unread count of Chat list.", e, selectedChatListTypeOrLeadershipUuid, activeChatListDetails);
            return 0;
        }
    };

    const employeeUnreadCount = getUnreadCount(USER_TYPE_EMPLOYEES);
    const clientUnreadCount = getUnreadCount(USER_TYPE_CLIENTS);

    const hasFullAccess = (permissions) => permissions.includes(FULL_ACCESS_BUSINESS_CHAT);
    const clientChatListDetails = activeChatListDetails.find(chatList => chatList.label === USER_TYPE_CLIENTS);
    
    const showClients = hasFullAccess(userPermissions) || userPermissions.includes(MESSAGE_CLIENTS)
        || (clientChatListDetails && clientChatListDetails.data.chats.length > 0);


    const renderActiveChatList = () => {

        if (activeChatListDetails.length === 0) {
            return <StyledMenuItem disabled value="No list available">
                <em>None</em>
            </StyledMenuItem>;
        }
        let prepareListItems = [];
        
        //employee
        const employeeDropdownOption = <StyledMenuItem key={USER_TYPE_EMPLOYEES} value={USER_TYPE_EMPLOYEES}>{capitalizeString(pluralize(customNames.employees))} { employeeUnreadCount > 0 && <UnreadCount unreadCount={employeeUnreadCount} />}</StyledMenuItem>;
        prepareListItems.push(employeeDropdownOption);

        //client
        if (showClients) {
            const clientDropdownOption = <StyledMenuItem key={USER_TYPE_CLIENTS} value={USER_TYPE_CLIENTS}>{capitalizeString(pluralize(customNames.clients))} { clientUnreadCount > 0 && <UnreadCount unreadCount={clientUnreadCount} />}</StyledMenuItem>;
            prepareListItems.push(clientDropdownOption);
        } 

        //leadership
        const leadershipActiveChats = activeChatListDetails.filter((activeChat) => ![USER_TYPE_CLIENTS, USER_TYPE_EMPLOYEES].includes(activeChat.label));
        
        const visibleLeadersOptions = leadershipActiveChats.map((activeChat) => {
            const role = leadershipRoles.find(leadershipRole => leadershipRole.uuid === activeChat.label);
            if (!role) {
                console.warn("Unable to create list item for role: ", activeChat.label);
                return false;
            }

            const isVisibleToThisUser = hasFullAccess(userPermissions) || role.visibleLeaderCount > 0 || role.currentUserIsLeader;
            const leadershipRoleUnreadCount = getUnreadCount(role.uuid);
            const menuContent = <>{ role.name } {leadershipRoleUnreadCount > 0 && <UnreadCount unreadCount={leadershipRoleUnreadCount} />}</>;
            
            if (isVisibleToThisUser) {
                return <StyledMenuItem 
                    key={role.uuid} 
                    value={role.uuid} 
                    name={role.name}> { menuContent }
                </StyledMenuItem>;
            }
        });

        prepareListItems.push(...visibleLeadersOptions);
        return prepareListItems.filter(Boolean);
    };

    const getTotalUnreadCount = (activeChatsTotalUnread) => {
        return activeChatsTotalUnread.reduce((sum, chat) => sum + chat.totalUnreadCount, 0);
    };

    const activeChatsUnreadCount = getTotalUnreadCount(activeChatsTotalUnread);

    const handleLeadershipRolesClick = () => {
        const modalData = currentUser[UserKeys.LEADERSHIP_ROLES] || [];
        if (props.toggleLeadershipModal) {
            props.toggleLeadershipModal(true, true, modalData);
        }
    };


    return (
        <>
            <div>
                <StyledText size="1rem" margin="1.4375rem 0 0.8125rem 0" weight="600">Active chats
                    <span style={{ display: "inline-block", marginLeft: "16px" }}>{activeChatsUnreadCount > 0 && <UnreadCount unreadCount={activeChatsUnreadCount} />}</span>
                </StyledText>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                { !isFetchingActiveChats ? 
                    <>
                        <ActiveChatListSelect value={selectedChatListTypeOrLeadershipUuid || ""} variant="outlined"
                            style={selectStyle}
                            onChange={handleSelectChange}
                            classes={{ outlined: classes.outlined }}
                            SelectDisplayProps={{
                                style: {
                                    background: "transparent",
                                },
                            }}
                        >
                            { renderActiveChatList() }
                        </ActiveChatListSelect>
                        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                            <CreateChatButtons key={"create-group-chat"}
                                onClick={() => {
                                    showCreateChatModalOrShowLeadershipEmployees(
                                        GROUP_CHAT,
                                        selectedChatListTypeOrLeadershipUuid
                                    );
                                }}
                                isAllowed={isChatActionAllowed(CREATE_GROUP_CHAT, currentUser[UserKeys.PERMISSIONS]) && showCreateGroupChatButton()}
                            >
                                <IconButton color="#fff" src={UserGroupIcon}></IconButton>
                            </CreateChatButtons>
                            
                            <CreateChatButtons key={"create-one-on-one-chat"}
                                onClick={() => {
                                    showCreateChatModalOrShowLeadershipEmployees(
                                        ONE_ON_ONE_CHAT,
                                        selectedChatListTypeOrLeadershipUuid
                                    );
                                }}
                                isAllowed={showCreateOneToOneChatButton()}
                            >
                                <IconButton color="#fff" src={UserSingleIcon}></IconButton>
                            </CreateChatButtons>
                        </div>
                    </>
                    : 
                    <>
                        <Skeleton style={{ height: selectStyle.height, width: selectStyle.width }} /> 
                    </>
                }
            </div>
            { /* Leadership Roles Button */ }
            {canSeeLeadershipRoles && (
                <LeadershipRolesButton onClick={() => console.log("Manage clicked")}>
                    <LeadershipInfo>
                        <LeadershipIcon src={UserIcon} alt="User Icon" width="40px" height="30px" />Leadership Roles <span style={{ color: "#8E4EB4", marginLeft: "6px" }}>({leadershipRoles?.length ?? 0})</span>
                    </LeadershipInfo>
                    <ManageButton onClick={() => handleLeadershipRolesClick()}>Manage</ManageButton>
                </LeadershipRolesButton>
            )}
        </>
    );
};

export default ActiveChats;
